import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Home, Info, X } from "lucide-react";
import { api } from "../api";
import { PageHead, LoadingBlock } from "../shell";

const nf = new Intl.NumberFormat("tr-TR");

export default function Quality({ user }) {
  const [data, setData] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const canDecide = ["admin", "manager"].includes(user?.role) && !user?.is_impersonating;

  const loadSummary = () => api.get("/analytics/quality").then((response) => setData(response.data));
  const loadDetails = (key) => api.get(`/analytics/quality/${key}`).then((response) => setDetails(response.data));
  useEffect(() => { loadSummary(); }, []);
  const openDrawer = (key) => {
    setOpenKey(key); setDetails(null); setFeedback(""); loadDetails(key);
  };
  const decide = async (item, decision) => {
    const actionKey = `${item.candidate_id}-${decision}`;
    setBusy(actionKey); setFeedback("");
    try {
      const response = await api.post("/policy-group-classification/housing-decision", {
        branch_name: item.branch_name, decision,
      });
      setFeedback(`${item.branch_name}: karar kaydedildi, ${nf.format(response.data.updated_record_count)} kayıt güncellendi.`);
      await Promise.all([loadSummary(), loadDetails("housing_candidate")]);
    } catch (error) {
      setFeedback(error.response?.data?.detail || "Konut kararı kaydedilemedi.");
    } finally { setBusy(""); }
  };

  return <main className="content quality-page">
    <PageHead eyebrow="GOVERNANCE" title="Veri Kalitesi" desc="Verinin güvenilirliğini, eksik alanları ve sınıflandırma adaylarını izleyin." />
    {!data ? <LoadingBlock/> : <>
      <div className="quality-strip"><div><span>Toplam kayıt</span><strong data-testid="quality-total">{nf.format(data.total_records)}</strong></div></div>
      <section className="quality-grid">
        {data.metrics.map((metric) => <button data-testid={`quality-metric-${metric.key}`} key={metric.key}
          className={`quality-card sev-${metric.severity}`} onClick={() => openDrawer(metric.key)}>
          <div className="qc-head">{metric.key === "housing_candidate" ? <Home size={17}/> : metric.severity === "good" ? <CheckCircle2 size={17}/> : metric.severity === "info" ? <Info size={17}/> : <AlertTriangle size={17}/>}<span>{metric.label}</span></div>
          <strong data-testid={`quality-metric-value-${metric.key}`}>{nf.format(metric.value)}</strong>
          <small>{metric.description || (metric.severity === "good" ? "Sorun yok" : metric.severity === "warning" ? "İnceleyin" : metric.severity === "info" ? "Bilgi" : "Kritik")}</small>
        </button>)}
      </section>
    </>}
    {openKey && <div className={`drawer ${openKey === "housing_candidate" ? "housing-candidate-drawer" : ""}`} data-testid="quality-detail-drawer">
      <div className="drawer-head"><h3 data-testid="quality-drawer-title">{openKey === "housing_candidate" ? "Konut olabilir mi?" : `Detay: ${openKey}`}</h3>
        <button data-testid="quality-drawer-close" className="icon-button" onClick={() => setOpenKey(null)}><X size={18}/></button></div>
      {!details ? <LoadingBlock/> : openKey === "housing_candidate"
        ? <HousingCandidates data={details} canDecide={canDecide} busy={busy} feedback={feedback} onDecide={decide}/>
        : <QualityRows items={details.items}/>} 
    </div>}
  </main>;
}

function HousingCandidates({ data, canDecide, busy, feedback, onDecide }) {
  return <div className="housing-quality-content">
    <div className="housing-quality-summary"><span>Bekleyen ad</span><strong data-testid="housing-pending-count">{nf.format(data.pending_count)}</strong><span>Etkilenen kayıt</span><strong data-testid="housing-affected-count">{nf.format(data.affected_record_count)}</strong></div>
    {feedback && <div data-testid="housing-decision-feedback" className="save-notice">{feedback}</div>}
    <CandidateSection title="Bekleyen adaylar" items={data.items} canDecide={canDecide} busy={busy} onDecide={onDecide}/>
    <CandidateSection title="Kaydedilmiş kararlar" items={data.decisions} canDecide={canDecide} busy={busy} onDecide={onDecide} decided/>
  </div>;
}

function CandidateSection({ title, items, canDecide, busy, onDecide, decided = false }) {
  return <section className="housing-candidate-section"><h4>{title}</h4>
    {!items.length && <div data-testid={`housing-${decided ? "decisions" : "candidates"}-empty`} className="empty-state">Kayıt bulunmuyor.</div>}
    {items.map((item) => <article className="housing-candidate" key={`${decided ? "decision" : "candidate"}-${item.candidate_id}`} data-testid={`housing-${decided ? "decision" : "candidate"}-${item.candidate_id}`}>
      <div className="housing-candidate-main"><strong data-testid={`housing-branch-${item.candidate_id}`}>{item.branch_name}</strong>
        <span>{nf.format(item.affected_count)} kayıt · {item.companies.slice(0, 3).join(" · ") || "Şirket bilgisi yok"}</span>
        <small>Örnek: {item.example?.policy_number || "—"} · {item.example?.insurance_company_name || "—"}</small></div>
      {decided && <div className={`housing-decision-state ${item.decision.toLowerCase()}`} data-testid={`housing-decision-state-${item.candidate_id}`}>{item.decision === "APPROVED" ? "Konut" : "Konut değil"}<small>{item.decided_by}</small></div>}
      {canDecide && <div className="housing-candidate-actions">
        <button data-testid={`housing-approve-${item.candidate_id}`} className="primary-button" disabled={busy || item.decision === "APPROVED"} onClick={() => onDecide(item, "APPROVED")}>{busy === `${item.candidate_id}-APPROVED` ? "Uygulanıyor…" : "Konut olarak onayla"}</button>
        <button data-testid={`housing-reject-${item.candidate_id}`} className="outline-button" disabled={busy || item.decision === "REJECTED"} onClick={() => onDecide(item, "REJECTED")}>{busy === `${item.candidate_id}-REJECTED` ? "Uygulanıyor…" : "Konut değil"}</button>
      </div>}
    </article>)}
  </section>;
}

function QualityRows({ items }) {
  return <div className="table-panel"><table data-testid="quality-detail-table"><thead><tr><th>Poliçe</th><th>Şirket</th><th>Satıcı</th><th>Tarih</th><th>Prim</th></tr></thead>
    <tbody>{items.map((item) => <tr key={item.id} data-testid={`quality-detail-row-${item.id}`}><td>{item.policy_number}</td><td>{item.insurance_company_name || "—"}</td><td>{item.seller_name || item.seller_name_raw || "—"}</td><td>{item.issue_date || "—"}</td><td>{nf.format(item.net_premium || 0)}</td></tr>)}</tbody></table>
    {!items.length && <div className="empty-state">Bu kategoride kayıt yok.</div>}</div>;
}